import { logEvent } from "firebase/analytics";
import { getAppAnalytics } from "./firebase";
import * as Sentry from "@sentry/react";
import { ProfileStats } from "../types";
import { isSentryCapturedError, markSentryCaptured } from "./sentryCapture";
import { shouldDropExpectedSentryError } from "./sentryPrivacy";

type TechnicalErrorTagValue = string | number | boolean | null | undefined;
type AnalyticsEventParams = Record<string, TechnicalErrorTagValue>;

interface LogTechnicalErrorOptions {
  tags?: Record<string, TechnicalErrorTagValue>;
  extra?: Record<string, unknown>;
  fingerprint?: string[];
}

const normalizeTags = (tags?: Record<string, TechnicalErrorTagValue>) => {
  if (!tags) return {};

  return Object.fromEntries(
    Object.entries(tags)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => [key, String(value)])
  );
};

const getErrorProperty = (error: unknown, key: string): unknown => {
  if (!error || typeof error !== 'object' || !(key in error)) return undefined;
  return (error as Record<string, unknown>)[key];
};

const getErrorStringProperty = (error: unknown, key: string): string | undefined => {
  const value = getErrorProperty(error, key);
  return typeof value === 'string' && value.trim() ? value : undefined;
};

const getTagNumberValue = (tags: Record<string, string>, key: string): number | undefined => {
  const value = tags[key];
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const getStatusFamily = (status: number | undefined): string | undefined => {
  if (!status || !Number.isFinite(status)) return undefined;
  const family = Math.floor(status / 100);
  return family >= 1 && family <= 5 ? `${family}xx` : 'other';
};

const getErrorArea = (context: string): string => {
  if (context.startsWith('api:sendChatMessage')) return 'chat_api';
  if (context.startsWith('api:uploadFileToFirebase')) return 'attachments';
  if (context.startsWith('subscriptionService:')) return 'subscription';
  if (context.startsWith('userService:')) return 'user_profile';
  if (context.startsWith('App:')) return 'app';
  return 'other';
};

const cleanAnalyticsParams = (params: AnalyticsEventParams): Record<string, string | number | boolean> => (
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null)
  ) as Record<string, string | number | boolean>
);

const logAnalyticsEvent = (eventName: string, params: AnalyticsEventParams) => {
  void getAppAnalytics()
    .then((appAnalytics) => {
      if (!appAnalytics) return;
      logEvent(appAnalytics, eventName, cleanAnalyticsParams(params));
    })
    .catch((error) => {
      if (import.meta.env.DEV) {
        console.warn("Analytics event skipped:", error);
      }
    });
};

/**
 * 0. ERRORES TÉCNICOS (Sentry + GA4)
 * Captura excepciones críticas en flujos de Stripe, Auth o Storage.
 */
export const logTechnicalError = (error: any, context: string, options: LogTechnicalErrorOptions = {}) => {
  try {
    // 0. Evitar duplicidad si el error ya fue capturado manualmente
    if (isSentryCapturedError(error)) return;
    if (shouldDropExpectedSentryError(error, context)) return;

    const isApiStatusContext = typeof context === 'string'
      && context.startsWith('api:sendChatMessage:http:');
    const requestId = getErrorStringProperty(error, 'requestId');
    const errorKind = getErrorStringProperty(error, 'errorKind');
    const tags = normalizeTags({
      request_id: requestId,
      error_kind: errorKind,
      ...options.tags
    });

    // 1. Reporte detallado a Sentry (técnico)
    Sentry.captureException(error, {
      extra: {
        context,
        requestId,
        errorKind,
        ...options.extra
      },
      tags: {
        source: 'manual',
        context,
        ...tags
      },
      // Agrupamos por status en /api/chat. En el resto, mantenemos el fingerprint default.
      fingerprint: options.fingerprint ?? (isApiStatusContext ? [context] : ['{{ default }}', context])
    });

    markSentryCaptured(error);

    const statusCode = getTagNumberValue(tags, 'status_code');
    // 2. Evento ligero en GA4 (estadístico)
    logAnalyticsEvent('technical_error', {
      error_context: context,
      error_area: getErrorArea(context),
      error_kind: errorKind || tags.error_kind,
      status_code: statusCode,
      http_status_code: statusCode ? String(statusCode) : undefined,
      http_status_family: getStatusFamily(statusCode),
      timestamp: new Date().toISOString()
    });
  } catch (e) {
    console.warn("Monitoring error:", e);
  }
};

/**
 * 1. ONBOARDING (Ya implementado)
 */
export const logOnboardingCompleted = (stats: Partial<ProfileStats>) => {
  try {
    logAnalyticsEvent('complete_onboarding', {
      police_body: stats.cuerpo,
      work_scope: stats.ambito,
      service_years: stats.añosServicio,
      age_range: stats.edad,
      region: stats.comunidadAutonoma,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.warn("Analytics error:", error);
  }
};

/**
 * 3. ELIMINACIÓN DE CUENTA
 * Se dispara justo antes de borrar al usuario.
 */
export const logAccountDeleted = () => {
  try {
    logAnalyticsEvent('account_deleted', {
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.warn("Analytics error:", error);
  }
};
